import React, { Fragment } from "react";
import { Button, Card, Col, Row } from "antd";
import { PlanType } from "../types/planType";
import { useModal } from "./context/CroModalContext";
import PlanDetails from "../pages/modals/PlanDetails";
import PlanDocument from "../pages/modals/PlanDocument";
import {
  cardBody,
  btnPrimary,
  btnOutline,
  callOutIconStyles,
  glowKeyframes,
  baseCardStyle,
  iconStyles,
  glowPseudoElementStyle,
  featuredCardClassName,
} from "./styles/cardStyles";
import { txCompare, usnCompare } from "../pages/rateCompare/compare";
import { S3_BUCKET } from "../config";
import { PlanDesc } from "../components/PlanDesc";
import RenderPlanIcon from "./plancards/renderPlanIcon";
import { Textfit } from "react-textfit";
import { useTranslation } from "react-i18next";

interface PlanCardProps {
  plan: PlanType;
  selectPlan?: () => void;
  isTx: boolean;
  isPa: boolean;
  zipCodeLoading?: boolean;
  isFeatured?: boolean;
  utilityId?: string;
  userZipCode?: string;
  isTypeVariable?: boolean;
  utilityName: string;
}

const PlanCard = ({
  plan,
  selectPlan,
  isTx,
  isPa,
  zipCodeLoading,
  isFeatured = false,
  utilityId,
  userZipCode,
  isTypeVariable = false,
  utilityName,
}: PlanCardProps) => {
  const { showModal } = useModal();
  const { t } = useTranslation("cro");

  const combinedStyle: React.CSSProperties = {
    ...baseCardStyle(plan, isFeatured),
    transition: "transform 0.3s ease",
  };

  const showPlanDocuments = (plan: PlanType) => {
    showModal(
      { title: "" },
      { content: <PlanDocument plan={plan} /> },
      [{ size: "xl" }],
      { hideHeader: true }
    );
  };

  const showPlanDetails = async (plan: PlanType) => {
    let marketPrice = 0;
    const txPrice = async () => {
      if (utilityId && plan) {
        await txCompare(parseFloat(utilityId), Number(plan.contract_term)).then(
          (res) => {
            marketPrice = parseFloat(res);
          }
        );
      }
    };
    const usnPrice = async () => {
      if (userZipCode && utilityId) {
        await usnCompare(Number(userZipCode), Number(utilityId)).then((res) => {
          marketPrice = res && parseFloat(res.total_average_price);
        });
      }
    };

    if (isTx) {
      await txPrice();
    } else {
      await usnPrice();
    }

    const monthlyEstData = [
      {
        key: "1",
        homeSize: (
          <>
            <span>
              {t("Apartment")}
              <br /> (500 kWh)
            </span>
          </>
        ),
        avgPrice: `${plan.price500}¢`,
        monthlySavings: `$${(
          (marketPrice * 500) / 100 -
          (plan.price500 * 500) / 100
        ).toFixed(2)}`,
      },
      {
        key: "2",
        homeSize: (
          <>
            <span>
              {t("1-2 Bedroom")}
              <br /> (1,000 kWh)
            </span>
          </>
        ),
        avgPrice: `${plan.price1000}¢`,
        monthlySavings: `$${(
          (marketPrice * 1000) / 100 -
          (plan.price1000 * 1000) / 100
        ).toFixed(2)}`,
      },
      {
        key: "3",
        homeSize: (
          <>
            <span>
              {t("3+ Bedroom")}
              <br /> (2,000 kWh)
            </span>
          </>
        ),
        avgPrice: `${plan.price2000}¢`,
        monthlySavings: `$${(
          (marketPrice * 2000) / 100 -
          (plan.price2000 * 2000) / 100
        ).toFixed(2)}`,
      },
    ];

    const filteredMonthlyEstData = monthlyEstData.filter((item) => {
      const monthlySavingsValue = parseFloat(
        item.monthlySavings.replace("$", "").replace(",", "").trim()
      );
      return monthlySavingsValue >= 5;
    });

    showModal(
      { title: "" },
      {
        content: (
          <PlanDetails
            plan={plan}
            utilityName={utilityName}
            selectPlan={selectPlan}
            utilityId={utilityId}
            isTx={isTx}
            isPa={isPa}
            userZipCode={userZipCode}
            isFeatured={isFeatured}
            isTypeVariable={isTypeVariable}
            filteredMonthlyEstData={filteredMonthlyEstData}
          />
        ),
      },
      ...(filteredMonthlyEstData.length > 0
        ? [{ size: "xl" }]
        : [{ size: "xl" }]),
      { hideHeader: true }
    );
  };

  let planBullets = plan?.ef_plan_details?.plan_bullets || [];

  if (
    plan.commodity_name === "Electric" &&
    plan?.ef_plan_details?.electric_savings &&
    planBullets.length > 3
  ) {
    planBullets = planBullets.slice(0, -1);
  }

  return (
    <>
      {isFeatured && <style>{glowKeyframes(plan)}</style>}
      {isFeatured && <style>{glowPseudoElementStyle(plan)}</style>}
      <Card
        style={combinedStyle}
        bodyStyle={{ padding: 0 }}
        className={isFeatured ? featuredCardClassName : ""}
      >
        <Row
          style={{
            ...cardBody,
            ...(isFeatured
              ? { backgroundColor: "#FFFDF1" }
              : { backgroundColor: "#E3F3FF" }),
          }}
        >
          <Col
            span={24}
            style={{
              color: "#253f66",
            }}
          >
            {" "}
            <Row style={{ width: "100%" }} justify={"start"} align={"top"}>
              <Col
                span={24}
                style={{
                  display: "flex",
                  alignItems: isFeatured ? "flex-start" : "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "nowrap",
                  gap: "0.5rem",
                }}
              >
                <div
                  style={{
                    flex: "1 1 auto",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      flex: "1 1 auto",
                      minWidth: 0,
                    }}
                  >
                    <Textfit
                      mode="multi"
                      min={12}
                      max={20}
                      className="roboto-semibold"
                      style={{ width: "100%" }}
                    >
                      {plan.plan_name}
                    </Textfit>
                  </div>
                  <div
                    className="roboto-semibold"
                    style={{
                      color: "#253f66",
                      marginTop: "-0.5rem",
                      flex: "0 0 auto",
                    }}
                  >
                    <Textfit
                      mode="single"
                      min={18}
                      max={46}
                      style={{
                        color: "#253f66",
                        fontWeight: 500,
                        width: "100%",
                      }}
                      className="roboto-bold"
                    >
                      {plan.price2000}&cent;
                      <span
                        style={{ fontSize: "1rem", fontWeight: "500" }}
                        className="roboto-bold"
                      >
                        {t("per")}
                        {String(utilityId) === "84" ? "ccf" : plan.unit}
                      </span>
                    </Textfit>
                    {plan.commodity_name === "Electric" && isTx && (
                      <p
                        className="roboto-regular"
                        style={{
                          color: "#253F66",
                          fontWeight: 450,
                          fontSize: "0.875rem",
                        }}
                      >
                        {t("based on 2,000 kWh of estimated usage")}
                      </p>
                    )}
                  </div>
                </div>{" "}
                <div
                  style={{
                    flex: "0 0 auto",
                    minWidth: "60px",
                    maxWidth: "120px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {isFeatured ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div>
                        <img
                          src={
                            plan.commodity_name === "Gas"
                              ? "/icon/featured-gas.png"
                              : "/icon/featured.png"
                          }
                          alt="Icon"
                          style={{
                            ...iconStyles,
                            width: "clamp(50px, 8vw, 50px)",
                            height: "clamp(50px, 8vw, 50px)",
                          }}
                        />
                      </div>{" "}
                      {plan.commodity_name === "Electric" && (
                        <div
                          className="roboto-bold"
                          style={{
                            textAlign: "center",
                            fontSize: "clamp(0.625rem, 1.8vw, 0.75rem)",
                            lineHeight: "1.1",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {plan.ef_plan_details?.callout_text ? (
                            <span>
                              {t(`${plan.ef_plan_details?.callout_text}`)}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <RenderPlanIcon plan={plan} utilityName={utilityName} />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        {/* Bullets */}
        {plan.ef_plan_details?.plan_bullets && planBullets?.length > 0 ? (
          <div
            style={{
              padding: "1rem",
              color: "#253f66",
              minHeight: plan?.commodity_name === "Gas" ? 150 : 175,
            }}
          >
            {RenderFeaturedGasBullet(isFeatured, plan)}
            {RenderFeaturedElectricBullet(plan)}
            {planBullets?.map((item, index) => (
              <Row align="middle" gutter={[16, 16]} key={index}>
                <Col
                  span={3}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={S3_BUCKET + item.icon}
                    style={callOutIconStyles(plan)}
                    alt="Icon"
                  />
                </Col>
                <Col span={18} className="roboto-regular">
                  <PlanDesc desc={item.text} />
                </Col>
              </Row>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: "1rem",
              color: "#253f66",
              minHeight: 175,
            }}
          >
            {RenderFeaturedGasBullet(isFeatured, plan)}
            {RenderFeaturedElectricBullet(plan)}
            {plan.commodity_name === "Electric" &&
              RenderElectricStatic(plan, isTypeVariable)}
            {plan.commodity_name === "Gas" && RenderGasStatic(plan)}
          </div>
        )}
        {/* Card footer */}
        <Row
          align={"middle"}
          justify="space-between"
          wrap
          style={{ marginBottom: "1rem" }}
        >
          <Col style={{ margin: "0 auto" }}>
            <Button
              type="link"
              onClick={() =>
                plan.commodity_name === "Gas"
                  ? showPlanDocuments(plan)
                  : showPlanDetails(plan)
              }
              style={{ ...btnPrimary, width: "100%", fontWeight: 500 }}
            >
              {plan.commodity_name === "Gas"
                ? t("PLAN DOCUMENTS")
                : t("MORE DETAILS")}
            </Button>
          </Col>
          <Col style={{ margin: "0 auto" }}>
            <Button
              style={{
                ...btnOutline,
                width: "100%",
                fontWeight: 500,
                ...(isFeatured
                  ? { backgroundColor: plan.ef_plan_details?.callout_color }
                  : {}),
              }}
              onClick={selectPlan}
              loading={zipCodeLoading}
            >
              {t("SELECT PLAN")}
            </Button>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default PlanCard;

const RenderFeaturedGasBullet = (isFeatured: any, plan: PlanType) => {
  const { i18n } = useTranslation("cro");
  return (
    isFeatured &&
    plan.commodity_name === "Gas" && (
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/piggy-bank.svg"}
            style={callOutIconStyles(plan)}
            alt="piggy bank"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          {i18n.language === "en"
            ? plan.ef_plan_details?.callout_text
            : plan.ef_plan_details?.callout_text_spanish}
        </Col>
      </Row>
    )
  );
};

export const RenderFeaturedElectricBullet = (plan: PlanType) => {
  const { i18n } = useTranslation("cro");
  return (
    plan?.ef_plan_details?.electric_savings &&
    plan.commodity_name === "Electric" && (
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/piggy-bank.svg"}
            style={callOutIconStyles(plan)}
            alt="piggy bank"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          {i18n.language === "en"
            ? plan.ef_plan_details?.electric_savings
            : plan.ef_plan_details?.electric_savings_spanish}
        </Col>
      </Row>
    )
  );
};

const RenderElectricStatic = (plan: PlanType, isTypeVariable: any) => {
  const { t } = useTranslation("cro");
  return (
    <Fragment>
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/lock.svg"}
            style={callOutIconStyles(plan)}
            alt="Lock"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          {isTypeVariable
            ? "Plan Type: Variable"
            : t("month locked-in rate", { term: plan.contract_term })}
        </Col>
      </Row>
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={
              "https://d2w6juk2bn3su3.cloudfront.net/icon/check-circle-fill.svg"
            }
            style={callOutIconStyles(plan)}
            alt="Renewable"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          <span>{t("100% renewable")}</span>
        </Col>
      </Row>
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/slash-circle.svg"}
            style={callOutIconStyles(plan)}
            alt="DND"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          <span>{t("No base charges or usage fees")}</span>
        </Col>
      </Row>
    </Fragment>
  );
};

const RenderGasStatic = (plan: PlanType) => {
  const { t } = useTranslation("cro");
  return (
    <Fragment>
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/lock.svg"}
            style={callOutIconStyles(plan)}
            alt="Lock"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          {t("month locked-in rate", { term: plan.contract_term })}
        </Col>
      </Row>
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/Fire.svg"}
            style={callOutIconStyles(plan)}
            alt="Renewable"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          <span>{t("Carbon Neutral")}</span>
        </Col>
      </Row>
      <Row align="middle" gutter={[16, 16]}>
        <Col
          span={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={"https://d2w6juk2bn3su3.cloudfront.net/icon/slash-circle.svg"}
            style={callOutIconStyles(plan)}
            alt="DND"
          />
        </Col>
        <Col span={18} className="roboto-regular">
          <span>{t("No hidden fees")}</span>
        </Col>
      </Row>
    </Fragment>
  );
};
